/**
 * AppContext — single source of truth for:
 *   - Amazon Connect Agent SDK (contactClient, agentClient)
 *   - Authentication state (JWT, agentName)
 *   - Feature flags (seeded from /api/config at mount, refreshed via /api/features post-auth)
 *
 * Consumers: any route. Pages that run inside a Connect iframe (AgentApp) read
 * the SDK clients and attach their own contact event handlers. Pages that run
 * standalone (NotepadTestPage) simply call authenticate() with demo credentials
 * and read isFeatureEnabled().
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { AgentClient, ContactClient } from "@amazon-connect/contact";
import type { AppControllerClient } from "@amazon-connect/app-controller";

import type { AppConfig } from "../lib/config";
import { loadFeatures } from "../lib/config";
import {
  authenticateConnect,
  clearAuthToken,
  getAuthToken,
} from "../lib/api";
import { isAllowlistedAgent } from "../lib/agent";
import { trackEvent, setIdentityContext } from "../lib/analytics";

// =============================================================================
// Client-side feature overrides
// =============================================================================

/**
 * Per-feature client-side gates applied on top of the server flag.
 * `isFeatureEnabled(name)` returns true only if the server flag is on AND
 * the override (if any) also passes. Keep this table small — these are
 * UX-only gates; authoritative gating lives in the backend.
 */
const CLIENT_OVERRIDES: Record<string, (agentArn: string | null) => boolean> = {
  customer_360: isAllowlistedAgent,
};

// =============================================================================
// Auth token helpers
// =============================================================================

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

/** True when a valid, unexpired JWT exists for the given contactId. */
export function isTokenValidForContact(contactId: string): boolean {
  const token = getAuthToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const now = Math.floor(Date.now() / 1000);
  const exp = payload.exp as number | undefined;
  if (exp && exp < now + 60) return false;
  return payload.cid === contactId;
}

// =============================================================================
// Context
// =============================================================================

interface AppContextValue {
  // Connect SDK (null until init resolves; null forever when outside a Connect iframe)
  contactClient: ContactClient | null;
  agentClient: AgentClient | null;
  appController: AppControllerClient | null;
  /** Open a specific case in the Amazon Connect Cases app tab. No-op when outside Connect. */
  launchCase: (caseId: string) => Promise<void>;
  /** Open a specific profile in the Amazon Connect Customer Profiles app tab. No-op when outside Connect. */
  launchCustomerProfile: (profileId: string) => Promise<void>;
  /** contactId from the initial onCreate scope (if any). */
  initialContactId: string | null;
  /** Channel type of the initial contact (e.g. "voice", "chat"). */
  contactChannel: string | null;
  sdkError: string | null;
  isSdkInitializing: boolean;

  // Agent identity (auto-fetched once SDK ready)
  agentName: string | null;
  agentArn: string | null;
  /** Hydrate the agent ARN externally (used by the popout window which
   *  has no Connect SDK and receives the ARN via postMessage from the
   *  parent). Required for client-side feature overlays to evaluate. */
  setAgentArnFromExternal: (arn: string | null) => void;

  // Auth
  isAuthenticated: boolean;
  authenticate: (signature: string, callerArn: string, contactId: string) => Promise<void>;
  clearAuth: () => void;

  // Features
  isFeatureEnabled: (name: string) => boolean;
  refreshFeatures: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

interface AppContextProviderProps {
  config: AppConfig;
  children: ReactNode;
}

export function AppContextProvider({ config, children }: AppContextProviderProps) {
  // SDK
  const [contactClient, setContactClient] = useState<ContactClient | null>(null);
  const [agentClient, setAgentClient] = useState<AgentClient | null>(null);
  const [appController, setAppController] = useState<AppControllerClient | null>(null);
  const [initialContactId, setInitialContactId] = useState<string | null>(null);
  const [contactChannel, setContactChannel] = useState<string | null>(null);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [isSdkInitializing, setIsSdkInitializing] = useState(true);

  // Identity / auth / features
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentArn, setAgentArn] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [features, setFeatures] = useState<AppConfig["features"]>(config.features ?? {});

  // Avoid double SDK init under React StrictMode.
  const sdkInitStarted = useRef(false);

  // --- SDK init (runs once) ------------------------------------------------
  useEffect(() => {
    if (sdkInitStarted.current) return;
    sdkInitStarted.current = true;

    // No `cancelled` flag here: AppContext sits at the React root and doesn't
    // unmount during normal navigation, so the only "unmount" that fires is
    // StrictMode's fake one. If we guarded on `cancelled`, onCreate (which
    // runs AFTER the fake cleanup) would see cancelled=true and skip the
    // state setters — leaving agentClient null forever.
    (async () => {
      try {
        console.log("[AppContext] initializing Connect SDK...");
        const { AmazonConnectApp } = await import("@amazon-connect/app");
        const { ContactClient: CC, AgentClient: AC } = await import("@amazon-connect/contact");
        const { AppControllerClient: ACC } = await import("@amazon-connect/app-controller");

        const { provider } = AmazonConnectApp.init({
          onCreate: async (event) => {
            console.log("[AppContext] SDK onCreate fired", event);
            const scope = event.context?.scope;
            setContactClient(new CC(provider));
            setAgentClient(new AC(provider));
            setAppController(new ACC(provider));
            setIsSdkInitializing(false);
            if (scope?.type === "contact" && scope?.contactId) {
              setInitialContactId(scope.contactId);
            }
          },
        });
        console.log("[AppContext] AmazonConnectApp.init returned, waiting for onCreate");
      } catch (err) {
        console.warn("[AppContext] SDK init failed:", err);
        setSdkError(err instanceof Error ? err.message : String(err));
        setIsSdkInitializing(false);
      }
    })();
  }, []);

  // --- Fetch agent identity once agentClient is ready ----------------------
  useEffect(() => {
    if (!agentClient) return;
    let cancelled = false;
    Promise.all([agentClient.getName(), agentClient.getARN()])
      .then(([name, arn]) => {
        if (cancelled) return;
        console.log("[AppContext] agent:", { name, arn });
        setAgentName(name ?? null);
        setAgentArn(arn ?? null);

        // Emit a one-shot analytics event so we can observe how often the
        // client-side allowlist override actually flips a gate. Fires
        // regardless of override outcome (allow vs deny) to give us a
        // denominator in PostHog.
        for (const [feature, override] of Object.entries(CLIENT_OVERRIDES)) {
          const allowed = override(arn ?? null);
          console.log(`[AppContext] client override ${feature} → ${allowed ? "allow" : "deny"}`);
          trackEvent("client_override_evaluated", {
            feature,
            allowed,
            agent_arn: arn ?? null,
            agent_name: name ?? null,
          });
        }
      })
      .catch((err) => { console.warn("[AppContext] get agent identity failed:", err); });
    return () => { cancelled = true; };
  }, [agentClient]);

  // --- Attach agent ARN + contact ID to all analytics events ----------------
  useEffect(() => {
    setIdentityContext({ agentArn, contactId: initialContactId });
  }, [agentArn, initialContactId]);

  // --- Fetch contact channel type once SDK + contactId are ready ------------
  useEffect(() => {
    if (!contactClient || !initialContactId) return;
    let cancelled = false;
    contactClient
      .getChannelType(initialContactId)
      .then((ct) => {
        if (cancelled) return;
        console.log("[AppContext] channel:", ct);
        setContactChannel(ct?.type ?? null);
      })
      .catch((err) => { console.warn("[AppContext] getChannelType failed:", err); });
    return () => { cancelled = true; };
  }, [contactClient, initialContactId]);

  // --- Features ------------------------------------------------------------
  // Keep a ref of the latest features so `isFeatureEnabled` can have a stable
  // identity. Without this, any effect that lists isFeatureEnabled as a
  // dependency re-runs every time a flag refresh updates state — and if that
  // effect triggers another refresh (e.g. via authenticate()), you get an
  // infinite loop.
  const featuresRef = useRef(features);
  featuresRef.current = features;

  // agentArn is read inside the stable isFeatureEnabled callback, so we need
  // a ref to pick up the latest value without changing the callback identity.
  const agentArnRef = useRef(agentArn);
  agentArnRef.current = agentArn;

  const refreshFeatures = useCallback(async () => {
    try {
      setFeatures(await loadFeatures());
    } catch (err) {
      console.warn("[AppContext] refreshFeatures failed:", err);
    }
  }, []);

  const isFeatureEnabled = useCallback(
    (name: string) => {
      if (featuresRef.current[name]?.enabled !== true) return false;
      const override = CLIENT_OVERRIDES[name];
      // When a client override is configured, it must pass. If the agent
      // ARN isn't known yet (popout pre-bootstrap, or outside Connect),
      // deny by default — never bypass the allowlist.
      if (override) return override(agentArnRef.current);
      return true;
    },
    [],
  );

  // --- Auth ----------------------------------------------------------------
  const authenticate = useCallback(
    async (signature: string, callerArn: string, contactId: string) => {
      const data = await authenticateConnect({ signature, callerArn, contactId });
      if (!data.success || !data.token) {
        throw new Error("Invalid auth response");
      }
      setToken(data.token);
    },
    [],
  );

  const clearAuth = useCallback(() => {
    setToken(null);
    clearAuthToken();
  }, []);

  // Refresh features whenever a token becomes available (covers both the
  // normal authenticate() flow and the popout bootstrap path where the
  // token is hydrated into sessionStorage before React mounts).
  useEffect(() => {
    if (token) refreshFeatures();
  }, [token, refreshFeatures]);

  // --- Workspace app deep-linking -----------------------------------------
  // Cache resolved app ARNs across calls; getAppCatalog() involves
  // cross-frame messaging and ARNs don't change within a session.
  const appArnCacheRef = useRef<Map<string, string>>(new Map());

  const launchWorkspaceApp = useCallback(
    async (
      label: string,
      nameMatcher: RegExp,
      parameters: Record<string, string>,
      launchKey: string,
    ) => {
      if (!appController) {
        console.warn(`[AppContext] launch ${label}: Connect SDK not available (outside workspace?)`);
        return;
      }
      try {
        const runningApps = await appController.getApps();
        console.log(`[DEBUG]`, runningApps)

        // Reuse a prior resolution. Catalog ARNs use launchApp (supports
        // parameters); built-in running-app instanceIds use focusApp.
        const cached = appArnCacheRef.current.get(label);
        if (cached) {
          if (cached.startsWith("instance:")) {
            await appController.focusApp(cached.slice("instance:".length));
          } else {
            await appController.launchApp(cached, { parameters, launchKey });
          }
          trackEvent(`${label}_opened_in_workspace`, parameters);
          return;
        }

        // Try the launchable-apps catalog first — supports passing parameters.
        const catalog = await appController.getAppCatalog();
        const launchable = catalog.find((a) => nameMatcher.test(a.name));
        if (launchable) {
          appArnCacheRef.current.set(label, launchable.arn);
          await appController.launchApp(launchable.arn, { parameters, launchKey });
          trackEvent(`${label}_opened_in_workspace`, parameters);
          return;
        }

        // Fall back to running apps. Built-in panels (CustomerProfiles,
        // Cases) aren't in the catalog but are auto-started by the
        // workspace. We can only focusApp — the 3P SDK doesn't support
        // navigating within a built-in app to a specific record.
        const running = await appController.getApps();
        const runningApp = running.find((a) => {
          const name = (a as { config?: { name?: string }; name?: string }).config?.name
            ?? (a as { name?: string }).name;
          return name ? nameMatcher.test(name) : false;
        });
        if (!runningApp) {
          console.warn(`[AppContext] launch ${label}: no match in catalog or running apps for ${nameMatcher}`);
          return;
        }
        const instanceId = (runningApp as { instanceId: string }).instanceId;
        appArnCacheRef.current.set(label, `instance:${instanceId}`);
        await appController.focusApp(instanceId);
        trackEvent(`${label}_focused_in_workspace`, parameters);
      } catch (err) {
        console.warn(`[AppContext] launch ${label} failed:`, err);
      }
    },
    [appController],
  );

  const launchCase = useCallback(
    (caseId: string) => launchWorkspaceApp("case", /cases/i, { caseId }, "cases"),
    [launchWorkspaceApp],
  );

  const launchCustomerProfile = useCallback(
    (profileId: string) =>
      launchWorkspaceApp(
        "customer_profile",
        /customer\s*profiles?/i,
        { profileId },
        "customer-profiles",
      ),
    [launchWorkspaceApp],
  );

  const value: AppContextValue = {
    contactClient,
    agentClient,
    appController,
    launchCase,
    launchCustomerProfile,
    initialContactId,
    contactChannel,
    sdkError,
    isSdkInitializing,
    agentName,
    agentArn,
    setAgentArnFromExternal: setAgentArn,
    isAuthenticated: !!token,
    authenticate,
    clearAuth,
    isFeatureEnabled,
    refreshFeatures,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within <AppContextProvider>");
  return ctx;
}
